import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAgentStatus } from '../api/playground';
import type { AgentStatusInfo } from '../types';

/**
 * Agent 运行时状态轮询 hook（左栏状态点 / 中栏提示条 / 右栏保存提示共用）。
 *
 * 行为：
 * - agentId 变化时立即拉取一次；
 * - 之后每 pollMs（默认 5s）轮询；页面不可见（document.visibilityState !== 'visible'）时暂停；
 * - 页面重新可见时立即刷新一次；
 * - agentId 为空 / 组件卸载时停止。
 *
 * 返回：
 * - status: 最近一次拉取的运行时状态（'running' | 'stopped' | 'none' | 其他后端状态）；
 *           尚未拉取到时为 null（区别于 'none'：无运行时）。
 * - info: 完整 AgentStatusInfo（endpoint_url / last_active_at / framework）。
 * - error: 最近一次请求失败信息（可选，组件一般只用 toast 兜底）。
 * - refresh: 手动触发立即刷新（事件处理中使用，如启动成功后）。
 */

export interface RuntimeStatusOptions {
  pollMs?: number;
  /** 默认 true；false 时只在 agentId 变化时拉一次 */
  poll?: boolean;
}

export function isRuntimeRunning(status: string | null | undefined): boolean {
  return status === 'running';
}

export function useAgentRuntimeStatus(agentId: string | null | undefined, options: RuntimeStatusOptions = {}) {
  const { pollMs = 5000, poll = true } = options;
  const [info, setInfo] = useState<AgentStatusInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  // 手动刷新（事件处理中调用）：立即拉取并更新状态
  const refresh = useCallback(async () => {
    if (!agentId) return;
    const seq = ++requestSeq.current;
    try {
      const data = await fetchAgentStatus(agentId);
      if (requestSeq.current !== seq) return;
      setInfo(data);
      setError(null);
    } catch (err) {
      if (requestSeq.current !== seq) return;
      const e = err as { message?: unknown };
      setError(typeof e.message === 'string' ? e.message : '状态获取失败');
    }
  }, [agentId]);

  // agentId 变化：立即拉一次（setState 均在 .then/.catch 回调内，避免渲染级联）
  useEffect(() => {
    let cancelled = false;
    const promise = agentId ? fetchAgentStatus(agentId) : Promise.resolve(null);
    promise
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setInfo(null);
          return;
        }
        setInfo(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { message?: unknown };
        setError(typeof e.message === 'string' ? e.message : '状态获取失败');
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // 轮询：页面可见时按间隔拉取（setInterval 回调非 effect 主体，不受 set-state-in-effect 约束）
  useEffect(() => {
    if (!poll || !agentId) return;
    const tick = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    const timer = window.setInterval(tick, pollMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll, agentId, pollMs, refresh]);

  return {
    status: info?.status ?? null,
    info,
    error,
    refresh,
  };
}
